import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import {
  WELCOME_LABELS,
  WELCOME_PARAMS,
  isWelcomeStatusConfirmed,
  resolveWelcomeDialog,
  welcomeArrivalFromSearch,
} from './welcomeDialog';

/** Intervalle et nombre de relectures du statut tant que le webhook n'a pas écrit le cache. */
const STATUS_RETRY_MS = 1500;
const STATUS_MAX_RETRIES = 10;

/**
 * Fenêtre « Bienvenue dans Hector », montée une fois dans `DashboardLayout`.
 * S'ouvre à l'arrivée après paiement (`?upgrade_success=true`, posé par Stripe)
 * ou après le démarrage de l'essai (`?trial_started=true`, posé par
 * `InscriptionPage`). Pilotée par l'URL seule : le paramètre est retiré à la
 * fermeture, donc la fenêtre n'apparaît qu'une fois et jamais à un rechargement
 * ultérieur.
 *
 * Quand le cabinet a des postes libres, elle propose d'inviter l'équipe — c'est le
 * seul moment où l'essai y incite, le bandeau « licences disponibles » n'existant
 * que pour un abonnement actif.
 */
export function BillingWelcomeDialog() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const arrival = welcomeArrivalFromSearch(searchParams);
  const { data: status, refetch } = useBillingStatus();

  const hasFirstAnswer = status !== undefined;
  const confirmed = arrival !== null && isWelcomeStatusConfirmed(arrival, status);

  // Le webhook Stripe écrit le cache d'abonnement une ou deux secondes après le
  // retour dans l'app, et `useBillingStatus` garde sa réponse 5 minutes : sans
  // relecture, la fenêtre resterait sur « Continuer » alors que des postes sont
  // libres. On relit donc tant que le statut ne reflète pas l'arrivée, un nombre
  // borné de fois (un statut qui ne vient pas laisse simplement « Continuer »).
  useEffect(() => {
    if (arrival === null || confirmed || !hasFirstAnswer) return undefined;
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      void refetch();
      if (attempts >= STATUS_MAX_RETRIES) clearInterval(timer);
    }, STATUS_RETRY_MS);
    return () => clearInterval(timer);
  }, [arrival, confirmed, hasFirstAnswer, refetch]);

  if (arrival === null) return null;

  const { paragraphs, offerInvite } = resolveWelcomeDialog(arrival, status);

  const dismiss = () => {
    const next = new URLSearchParams(searchParams);
    for (const param of WELCOME_PARAMS) next.delete(param);
    // `replace` : « Précédent » ne doit pas rouvrir la fenêtre.
    setSearchParams(next, { replace: true });
  };

  const invite = () => {
    // On nettoie d'abord l'URL d'arrivée dans l'historique, sinon revenir en
    // arrière depuis les Paramètres rouvrirait la fenêtre d'accueil.
    dismiss();
    // `openInvite` : Settings ouvre directement la fenêtre d'invitation.
    navigate('/settings#collaborators', { state: { openInvite: Date.now() } });
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) dismiss();
      }}
    >
      <DialogContent className="border-none sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader className="items-center text-center">
          <DialogTitle className="text-xl font-semibold tracking-tight">Bienvenue dans Hector</DialogTitle>
          {/* Des <span> en bloc : la description Radix est un <p>, qui ne peut pas en contenir d'autres. */}
          <DialogDescription className="px-4 py-2">
            {paragraphs.map((paragraph, index) => (
              <span key={paragraph} className={index === 0 ? 'block' : 'mt-2 block'}>
                {paragraph}
              </span>
            ))}
          </DialogDescription>
        </DialogHeader>
        {offerInvite ? (
          // Le pied garde la même hauteur que le bouton unique : quand le statut
          // arrive après l'ouverture, seule la seconde phrase s'ajoute.
          <DialogFooter className="gap-2 sm:space-x-0">
            <Button variant="ghost" onClick={dismiss}>
              {WELCOME_LABELS.later}
            </Button>
            <Button onClick={invite} className="bg-charcoal hover:bg-charcoal-medium sm:flex-1">
              {WELCOME_LABELS.invite}
            </Button>
          </DialogFooter>
        ) : (
          <DialogFooter>
            <Button onClick={dismiss} className="w-full bg-charcoal hover:bg-charcoal-medium">
              {WELCOME_LABELS.continue}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
